import React, { useState } from "react";
import { useGetCategoryQuery, useDeleteCategoryMutation, useCreateCategoryMutation, useUpdateCategoryMutation } from "state/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Edit, Trash2, Upload, Loader2, Image as ImageIcon, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Filter, Search } from "lucide-react";
import { toast } from 'react-toastify';
import CachedImage from "components/CachedImage";

const Category = () => {
  const { data: categories, isLoading, refetch } = useGetCategoryQuery();
  const [deleteCategory, { isLoading: isDeleting }] = useDeleteCategoryMutation();
  const [createCategory, { isLoading: isCreating }] = useCreateCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] = useUpdateCategoryMutation();

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Form states
  const [categoryName, setCategoryName] = useState("");
  const [categoryImage, setCategoryImage] = useState(null);
  const [imageName, setImageName] = useState("");

  // Debug: Log categories data
  console.log("Categories data:", categories);

  // Pagination calculations
  const totalItems = categories?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCategories = categories?.slice(startIndex, endIndex) || [];

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToFirstPage = () => {
    setCurrentPage(1);
  };

  const goToLastPage = () => {
    setCurrentPage(totalPages);
  };

  const goToPreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    console.log("Selected file:", file); // Debug log
    console.log("File details:", {
      name: file?.name,
      size: file?.size,
      type: file?.type
    });
    
    if (file) {
      setCategoryImage(file);
      setImageName(file.name);
      console.log("File set successfully:", file.name);
    } else {
      setCategoryImage(null);
      setImageName("");
      console.log("No file selected");
    }
  };

  const handleCreateCategory = async () => {
    if (!categoryName.trim()) {
      toast.error('Category name is required');
      return;
    }

    console.log("Creating category with:", { 
      name: categoryName, 
      image: categoryImage,
      imageType: categoryImage?.type,
      imageSize: categoryImage?.size
    }); // Debug log

    try {
      // Ensure we have the file
      if (!categoryImage) {
        console.log("No image selected, creating category without image");
      }
      
      const result = await createCategory({ name: categoryName, image: categoryImage });
      console.log("Create category result:", result); // Debug log
      toast.success('Category created successfully!');
      setIsCreateDialogOpen(false);
      setCategoryName("");
      setCategoryImage(null);
      setImageName("");
      refetch();
    } catch (error) {
      console.error('Error creating category:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        data: error.data
      });
      toast.error('Failed to create category');
    }
  };

  const handleUpdateCategory = async () => {
    if (!categoryName.trim()) {
      toast.error('Category name is required');
      return;
    }

    console.log("Updating category with:", { 
      name: categoryName, 
      image: categoryImage,
      imageType: categoryImage?.type,
      imageSize: categoryImage?.size
    }); // Debug log

    try {
      const result = await updateCategory({ name: categoryName, image: categoryImage });
      console.log("Update category result:", result); // Debug log
      toast.success('Category updated successfully!');
      setIsEditDialogOpen(false);
      setCategoryName("");
      setCategoryImage(null);
      setImageName("");
      setSelectedCategory(null);
      refetch();
    } catch (error) {
      console.error('Error updating category:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        data: error.data
      });
      toast.error('Failed to update category');
    }
  };

  const handleDeleteCategory = async () => {
    try {
      await deleteCategory(selectedCategory._id);
      toast.success('Category deleted successfully!');
      setIsDeleteDialogOpen(false);
      setSelectedCategory(null);
      refetch();
    } catch (error) {
      toast.error('Failed to delete category');
      console.error('Error deleting category:', error);
    }
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setCategoryName(category.name);
    setImageName(category.image || "");
    setIsEditDialogOpen(true);
  };

  const handleDelete = (category) => {
    setSelectedCategory(category);
    setIsDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setCategoryName("");
    setCategoryImage(null);
    setImageName("");
    setSelectedCategory(null);
  };

  // Helper function to get image URL
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header Section */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Category Management</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Manage and organize product categories across your platform
              </p>
            </div>
            <Button 
              onClick={() => setIsCreateDialogOpen(true)} 
              className="bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Categories</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{totalItems}</p>
                </div>
                <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                  <ImageIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Categories</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{totalItems}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">This Page</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{currentCategories.length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Table Card */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm">
          <CardHeader className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">Category List</CardTitle>
                <CardDescription className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} categories
                </CardDescription>
              </div>
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search categories..."
                    className="pl-10 w-64 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                  />
                </div>
                <Button variant="outline" size="sm" className="border-gray-300 dark:border-gray-600">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-600 dark:text-gray-400" />
                <span className="ml-3 text-gray-600 dark:text-gray-400">Loading categories...</span>
              </div>
            ) : categories && categories.length > 0 ? (
              <div>
                {/* Desktop Table */}
                <div className="overflow-x-auto">
                  <Table className="hidden lg:table">
                    <TableHeader>
                      <TableRow className="bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <TableHead className="font-semibold text-gray-900 dark:text-white">Image</TableHead>
                        <TableHead className="font-semibold text-gray-900 dark:text-white">Category Name</TableHead>
                        <TableHead className="font-semibold text-gray-900 dark:text-white">ID</TableHead>
                        <TableHead className="font-semibold text-gray-900 dark:text-white text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentCategories.map((category, index) => {
                        const imageSrc =
                          category.image || category.imageUrl || category.img || category.thumbnail;
                        
                        return (
                          <TableRow 
                            key={category._id} 
                            className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                          >
                                                         <TableCell className="py-2">
                               {imageSrc ? (
                                 <CachedImage
                                   src={imageSrc}
                                   alt={category.name}
                                   className="w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-600"
                                   fallbackClassName="w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-600"
                                 />
                               ) : (
                                 <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                   <ImageIcon className="w-5 h-5 text-gray-400" />
                                 </div>
                               )}
                             </TableCell>
                             <TableCell className="py-2">
                               <div>
                                 <p className="font-medium text-gray-900 dark:text-white text-sm">{category.name}</p>
                                 <p className="text-xs text-gray-500 dark:text-gray-400">Category</p>
                               </div>
                             </TableCell>
                             <TableCell className="py-2">
                               <code className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-1 rounded">
                                 {category._id}
                               </code>
                             </TableCell>
                             <TableCell className="py-2 text-right">
                                                             <div className="flex items-center justify-end space-x-1">
                                 <Button
                                   variant="outline"
                                   size="sm"
                                   onClick={() => handleEdit(category)}
                                   className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 h-8 px-2"
                                 >
                                   <Edit className="h-3 w-3 mr-1" />
                                   Edit
                                 </Button>
                                 <Button
                                   variant="outline"
                                   size="sm"
                                   onClick={() => handleDelete(category)}
                                   className="border-red-300 dark:border-red-600 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 h-8 px-2"
                                 >
                                   <Trash2 className="h-3 w-3 mr-1" />
                                   Delete
                                 </Button>
                               </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden space-y-4 p-4">
                  {currentCategories.map((category) => {
                    const imageSrc =
                      category.image || category.imageUrl || category.img || category.thumbnail;
                    
                    return (
                      <Card key={category._id} className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                        <CardContent className="p-4">
                          <div className="flex items-center space-x-4">
                            <div className="flex-shrink-0">
                              {imageSrc ? (
                                <CachedImage
                                  src={imageSrc}
                                  alt={category.name}
                                  className="w-16 h-16 rounded-lg border border-gray-200 dark:border-gray-600"
                                  fallbackClassName="w-16 h-16 rounded-lg border border-gray-200 dark:border-gray-600"
                                />
                              ) : (
                                <div className="w-16 h-16 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                  <ImageIcon className="w-8 h-8 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-gray-900 dark:text-white truncate">
                                {category.name}
                              </h3>
                                                             <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                 ID: {category._id}
                               </p>
                            </div>
                            <div className="flex flex-col space-y-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEdit(category)}
                                className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(category)}
                                className="border-red-300 dark:border-red-600 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
                      <span>
                        Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} results
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToFirstPage}
                        disabled={currentPage === 1}
                        className="border-gray-300 dark:border-gray-600"
                      >
                        <ChevronsLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className="border-gray-300 dark:border-gray-600"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      
                      {/* Page Numbers */}
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? "default" : "outline"}
                              size="sm"
                              onClick={() => goToPage(pageNum)}
                              className={`w-8 h-8 p-0 ${
                                currentPage === pageNum 
                                  ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900" 
                                  : "border-gray-300 dark:border-gray-600"
                              }`}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className="border-gray-300 dark:border-gray-600"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToLastPage}
                        disabled={currentPage === totalPages}
                        className="border-gray-300 dark:border-gray-600"
                      >
                        <ChevronsRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ImageIcon className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No categories found</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">Get started by creating your first category</p>
                <Button 
                  onClick={() => setIsCreateDialogOpen(true)}
                  className="bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Category
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Category Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="w-[95vw] max-w-md sm:max-w-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white">Create New Category</DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              Add a new product category to organize your products
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Category Name</label>
              <Input
                placeholder="Enter category name"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                className="mt-1 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Category Image</label>
              <div className="mt-1">
                <input
                  accept="image/*"
                  id="create-image"
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleImageChange}
                  name="file"
                  onClick={(e) => console.log("File input clicked")}
                />
                <Button 
                  variant="outline" 
                  className="w-full border-gray-300 dark:border-gray-600"
                  onClick={() => {
                    console.log("Button clicked, triggering file input");
                    document.getElementById('create-image').click();
                  }}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {imageName || "Select Image"}
                </Button>
                {imageName && (
                  <p className="text-xs text-gray-500 mt-1">Selected: {imageName}</p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setIsCreateDialogOpen(false);
                resetForm();
              }}
              className="w-full sm:w-auto border-gray-300 dark:border-gray-600"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateCategory} 
              disabled={isCreating}
              className="w-full sm:w-auto bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Category'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="w-[95vw] max-w-md sm:max-w-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white">Edit Category</DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              Update the category information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Category Name</label>
              <Input
                placeholder="Enter category name"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                className="mt-1 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Category Image</label>
              <div className="mt-1">
                <input
                  accept="image/*"
                  id="edit-image"
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleImageChange}
                  name="file"
                  onClick={(e) => console.log("Edit file input clicked")}
                />
                <Button 
                  variant="outline" 
                  className="w-full border-gray-300 dark:border-gray-600"
                  onClick={() => {
                    console.log("Edit button clicked, triggering file input");
                    document.getElementById('edit-image').click();
                  }}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {imageName || "Select Image"}
                </Button>
                {imageName && (
                  <p className="text-xs text-gray-500 mt-1">Selected: {imageName}</p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setIsEditDialogOpen(false);
                resetForm();
              }}
              className="w-full sm:w-auto border-gray-300 dark:border-gray-600"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateCategory} 
              disabled={isUpdating}
              className="w-full sm:w-auto bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
            >
              {isUpdating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Category'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="w-[95vw] max-w-md bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white">Delete Category</DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              Are you sure you want to delete "{selectedCategory?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setIsDeleteDialogOpen(false)}
              className="w-full sm:w-auto border-gray-300 dark:border-gray-600"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteCategory} 
              disabled={isDeleting}
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Category'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Category;
